import { GridColDef, GridValueGetterParams } from "@mui/x-data-grid";
import {
	createdAtmodifiedAtActions,
	enumToStringConverter,
	formatDate,
	moneyFormat,
} from "../../../../helpers/appHelpers";
import { Avatar } from "@mui/material";
import { STATUS } from "../../../../models/employee.dto";

export const columns: GridColDef[] = [
	{
		field: "employeeName",
		headerName: "Employee Name",
		minWidth: 260,
		flex: 1,
		headerClassName: "super-app-theme--header",
		renderCell: (params: any) => {
			return (
				<div className="flex items-center space-x-4">
					<Avatar
						src={params.row.photoUrl}
						alt="Dp"
						className="w-10 h-10 rounded-full"
					/>
					<p className="text-sm truncate">
						{params.row.firstName + " " + params.row.lastName}
					</p>
				</div>
			);
		},
	},
	{
		field: "gender",
		headerName: "Gender",
		minWidth: 150,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => enumToStringConverter(params.value),
	},
	{
		field: "title",
		headerName: "Title",
		minWidth: 180,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "dateOfBirth",
		headerName: "Date Of Birth",
		minWidth: 170,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => formatDate(params),
	},
	{
		field: "employeeNumber",
		headerName: "Employee Number",
		minWidth: 200,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "nationality",
		headerName: "Nationality",
		minWidth: 180,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "email",
		headerName: "Corporate Email Address",
		minWidth: 265,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "privateEmail",
		headerName: "Private Email Address",
		minWidth: 265,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "status",
		headerName: "Employee Status",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
		renderCell(params) {
			return (
				<div 
					className="rounded-lg px-4 py-2 min-w-[80px] text-center relative font-semibold overflow-hidden"
					style={{ color: getOrganizationStatusColor(params.value) }}
				>
					<div 
						className="absolute inset-0" 
						style={{ 
							background: getOrganizationStatusColor(params.value), 
							opacity: "15%"
						}}
					/>
					<p>{enumToStringConverter(params.value)}</p>
				</div>
			);
		},
	},
	{
		field: "netSalary",
		headerName: "Net Salary",
		type: "number",
		minWidth: 200,
		flex: 1,
		align: "left",
		headerAlign: "left",
		headerClassName: "super-app-theme--header",
		valueGetter(params) {
			const amount = params.row?.employeePayInfo?.basicPay;
			const currency = params.row?.employeePayInfo?.companyCurrency?.currency?.code;
			return (currency || "") + " " + (moneyFormat(amount) || "");
		},
	},
	{
		field: "msisdn",
		headerName: "Phone Number",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "alternateMsisdn",
		headerName: "Alternate Phone Number",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "ssn",
		headerName: "SSN",
		minWidth: 220,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "address",
		headerName: "Address",
		minWidth: 300,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "digitalAddress",
		headerName: "Digital Address",
		minWidth: 300,
		flex: 1,
		headerClassName: "super-app-theme--header",
	},
	{
		field: "employmentDate",
		headerName: "Employment Date",
		minWidth: 200,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => formatDate(params),
	},
	{
		field: "terminationDate",
		headerName: "Termination Date",
		minWidth: 200,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => params.value ? formatDate(params) : "--",
	},
	{
		field: "unionMember",
		headerName: "Union Member",
		minWidth: 180,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => params.value ? "Yes" : "No",
	},
	{
		field: "reemployed",
		headerName: "Reemployed",
		minWidth: 180,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => params.value ? "Yes" : "No",
	},
	{
		field: "resident",
		headerName: "Resident",
		minWidth: 180,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => params.value ? "Yes" : "No",
	},
	{
		field: "hr",
		headerName: "HR Personnel",
		minWidth: 180,
		flex: 1,
		headerClassName: "super-app-theme--header",
		valueGetter: (params) => params.value ? "Yes" : "No",
	},
	
	...createdAtmodifiedAtActions
];

export const getOrganizationStatusColor = (status: STATUS) => {
	switch(status) {
		case "ACTIVE":
			return "#4CAF50"
		case "TERMINATED":
			return "#808080"
		case "DISABLED":
			return "#F44336"
		case "ELAPSED_CONTRACT":
			return "#FFC107"
		case "ARCHIVED": 
			return "#2196F3"
		default: 
			return "#BF21F3"
	}
};

export const EmployeesColumnVisibilityModel: Record<string, boolean> = {
	employeeName: true,
	gender: true,
	title: false,
	dateOfBirth: false,
	employeeNumber: true,
	nationality: true,
	email: true,
	privateEmail: false,
	status: true,
	netSalary: true,
	msisdn: true,
	alternateMsisdn: false,
	ssn: false,
	address: false,
	digitalAddress: false,
	employmentDate: false,
	terminationDate: false,
	unionMember: false,
	reemployed: false,
	resident: false,
	hr: false,
	createdAt: true,
	modifiedAt: true,
};